import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';
import { LogoMark } from '@/components/Logo';

/* ======================================================================
   BOOT SEQUENCE — cinematic "system boot" on first load (once per session)
   ====================================================================== */
const BOOT_LINES = [
  { t: 'IT-WELT360 // SYSTEM BOOT v3.0', c: 'head' },
  { t: '[ OK ] secure-core kernel geladen', c: 'ok' },
  { t: '[ OK ] /network · 254 nodes verbunden', c: 'ok' },
  { t: '[ OK ] /security · firewall + ids aktiv', c: 'ok' },
  { t: '[ OK ] /cloud · DE-FRA1 online', c: 'ok' },
  { t: '[ OK ] /ai · NOVA initialisiert', c: 'ok' },
  { t: '>> alle systeme betriebsbereit', c: 'accent' },
];

export function BootSequence() {
  const reduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [visible, setVisible] = useState(() => {
    try { return !sessionStorage.getItem('itw_booted'); } catch { return true; }
  });
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [online, setOnline] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('itw_boot_sound') === '1'; } catch { return false; }
  });
  const audioRef = useRef(null);
  const startedRef = useRef(false);

  // Web Audio: subtle boot chime + soft ticks (no external asset)
  const playBoot = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = audioRef.current || new Ctx();
      audioRef.current = ctx;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.value = 0.0001;
      master.connect(ctx.destination);
      // gentle rising hum
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(180, now);
      osc.frequency.exponentialRampToValueAtTime(420, now + 1.8);
      const hum = ctx.createGain();
      hum.gain.setValueAtTime(0.0001, now);
      hum.gain.exponentialRampToValueAtTime(0.05, now + 0.4);
      hum.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);
      osc.connect(hum); hum.connect(master);
      osc.start(now); osc.stop(now + 2.3);
      // soft blips synced to lines
      for (let i = 0; i < 5; i++) {
        const t = now + 0.3 + i * 0.32;
        const b = ctx.createOscillator();
        b.type = 'triangle';
        b.frequency.setValueAtTime(660 + i * 90, t);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.06, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
        b.connect(g); g.connect(master);
        b.start(t); b.stop(t + 0.18);
      }
      // completion tone
      const done = ctx.createOscillator();
      done.type = 'sine';
      const dg = ctx.createGain();
      done.frequency.setValueAtTime(880, now + 2.0);
      dg.gain.setValueAtTime(0.0001, now + 2.0);
      dg.gain.exponentialRampToValueAtTime(0.09, now + 2.05);
      dg.gain.exponentialRampToValueAtTime(0.0001, now + 2.6);
      done.connect(dg); dg.connect(master);
      done.start(now + 2.0); done.stop(now + 2.65);
      master.gain.exponentialRampToValueAtTime(0.9, now + 0.05);
    } catch { /* audio not available */ }
  }, []);

  // check admin setting: skip boot entirely if disabled
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/settings/public`).then((r) => r.json()).then((s) => {
      if (cancelled) return;
      if (s && s.boot_enabled === false) {
        try { sessionStorage.setItem('itw_booted', '1'); } catch {}
        setVisible(false);
        return;
      }
      if (s && typeof s.boot_sound_default === 'boolean' && localStorage.getItem('itw_boot_sound') === null) {
        setSoundOn(s.boot_sound_default);
      }
    }).catch(() => { /* keep boot on network error */ });
    return () => { cancelled = true; };
  }, [visible]);

  const toggleSound = (e) => {
    e.stopPropagation();
    setSoundOn((prev) => {
      const next = !prev;
      try { localStorage.setItem('itw_boot_sound', next ? '1' : '0'); } catch {}
      if (next && audioRef.current && audioRef.current.state === 'suspended') audioRef.current.resume();
      if (next && !startedRef.current) { startedRef.current = true; playBoot(); }
      return next;
    });
  };

  useEffect(() => {
    if (!visible) return;
    const finish = () => { try { sessionStorage.setItem('itw_booted', '1'); } catch {} setVisible(false); };
    if (reduced) { const t = setTimeout(finish, 400); return () => clearTimeout(t); }

    if (soundOn && !startedRef.current) { startedRef.current = true; playBoot(); }

    const timers = [];
    BOOT_LINES.forEach((_, i) => timers.push(setTimeout(() => setCount(i + 1), 180 + i * 210)));
    let p = 0;
    const pi = setInterval(() => { p = Math.min(100, p + 4 + Math.random() * 7); setProgress(Math.round(p)); if (p >= 100) clearInterval(pi); }, 55);
    const onlineT = setTimeout(() => setOnline(true), 1650);
    const endT = setTimeout(finish, 2350);
    timers.push(pi, onlineT, endT);
    return () => timers.forEach((t) => clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, reduced]);

  const color = (c) => ({ head: '#EAF1FF', ok: '#2EF2A5', accent: '#00E7FF' }[c] || '#A9B6CC');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="boot-sequence"
          initial={{ opacity: 1 }}
          exit={{ y: '-100%', transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] } }}
          className="fixed inset-0 z-[100] bg-[#05070a] flex items-center justify-center overflow-hidden"
          onClick={() => { try { sessionStorage.setItem('itw_booted', '1'); } catch {} setVisible(false); }}
        >
          <div className="absolute inset-0 itw-grid opacity-30" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(600px 400px at 50% 45%, rgba(0,231,255,0.10), transparent 70%)' }} />
          <div className="relative w-[92%] max-w-lg px-2">
            <div className="flex items-center gap-3 mb-6">
              <LogoMark size={52} ring glow />
              <span className="font-display font-semibold text-lg text-[color:var(--itw-text)]">IT-WELT<span className="text-[color:var(--itw-cyan)]">360</span></span>
            </div>
            <div className="font-mono text-[13px] leading-7 min-h-[190px]">
              {BOOT_LINES.slice(0, count).map((l, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} style={{ color: color(l.c) }}>{l.t}</motion.div>
              ))}
              {!online && count > 0 && <span className="inline-block w-2 h-4 -mb-0.5 bg-[color:var(--itw-cyan)] animate-blink" />}
            </div>
            <div className="mt-5">
              <div className="h-1.5 rounded-full bg-[color:var(--itw-surface-2)] overflow-hidden border border-[color:var(--itw-border)]">
                <div className="h-full rounded-full transition-[width] duration-75" style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#1452A6,#00E7FF)' }} />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[11px] text-[color:var(--itw-text-3)]">
                <span>{online ? 'SYSTEM ONLINE' : 'booting…'}</span><span>{progress}%</span>
              </div>
            </div>
            <AnimatePresence>
              {online && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  className="mt-6 text-center font-display text-2xl font-semibold text-[color:var(--itw-cyan)] text-glow tracking-tight">
                  SYSTEM ONLINE
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button type="button" onClick={toggleSound} data-testid="boot-sound-toggle"
            className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full border border-[color:var(--itw-border)] px-3 py-1.5 font-mono text-[11px] text-[color:var(--itw-text-3)] hover:text-[color:var(--itw-text)] hover:border-[color:var(--itw-border-strong)] transition-colors">
            {soundOn ? <Volume2 className="h-3.5 w-3.5 text-[color:var(--itw-cyan)]" /> : <VolumeX className="h-3.5 w-3.5" />}
            Sound: {soundOn ? 'On' : 'Off'}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ======================================================================
   CURSOR GLOW — soft light that follows the pointer (desktop only)
   ====================================================================== */
export function CursorGlow() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    let raf, x = window.innerWidth / 2, y = window.innerHeight / 2, tx = x, ty = y;
    const move = (e) => { tx = e.clientX; ty = e.clientY; };
    const loop = () => {
      x += (tx - x) * 0.14; y += (ty - y) * 0.14;
      if (ref.current) ref.current.style.transform = `translate3d(${x - 320}px, ${y - 320}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener('mousemove', move, { passive: true });
    loop();
    return () => { window.removeEventListener('mousemove', move); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={ref} aria-hidden className="pointer-events-none fixed top-0 left-0 z-[35] h-[640px] w-[640px] rounded-full will-change-transform hidden md:block"
      style={{ background: 'radial-gradient(circle, rgba(0,231,255,0.09), rgba(0,231,255,0.03) 40%, transparent 62%)', mixBlendMode: 'screen' }} />
  );
}

/* ======================================================================
   ROUTE SCAN — cyan "scanning" sweep on every navigation
   ====================================================================== */
export function RouteScan() {
  const { pathname } = useLocation();
  return (
    <motion.div key={pathname} aria-hidden
      initial={{ y: '-40vh', opacity: 0.85 }}
      animate={{ y: '140vh', opacity: 0 }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
      className="pointer-events-none fixed inset-x-0 z-[46] h-48"
      style={{ background: 'linear-gradient(180deg, transparent, rgba(0,231,255,0.16) 45%, rgba(90,243,255,0.28) 50%, rgba(0,231,255,0.16) 55%, transparent)' }} />
  );
}

/* ======================================================================
   SCROLL PROGRESS — thin cyan bar at top
   ====================================================================== */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 26 });
  return (
    <motion.div aria-hidden className="fixed top-0 left-0 right-0 z-[55] h-[2px] origin-left"
      style={{ scaleX, background: 'linear-gradient(90deg,#1452A6,#00E7FF,#5AF3FF)' }} />
  );
}

/* ======================================================================
   TILT — 3D perspective tilt following the cursor
   ====================================================================== */
export function Tilt({ children, max = 7, className = '' }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(1100px) rotateY(${px * max}deg) rotateX(${-py * max}deg) translateZ(0)`;
  };
  const reset = () => { if (ref.current) ref.current.style.transform = 'perspective(1100px) rotateY(0deg) rotateX(0deg)'; };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className={className}
      style={{ transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1)', willChange: 'transform' }}>
      {children}
    </div>
  );
}
